import axios from 'axios'
import { ElMessage } from 'element-plus'
import { useUserStore } from '@/stores/user'

// 创建axios实例
const api = axios.create({
    baseURL: `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000'}/api`,
    timeout: 10000,
    headers: {
        'Content-Type': 'application/json'
    }
})

// 请求拦截器
api.interceptors.request.use(
    config => {
        const userStore = useUserStore()
        if (userStore.token) {
            config.headers.Authorization = `Bearer ${userStore.token}`
        }
        return config
    },
    error => {
        console.error('请求错误:', error)
        return Promise.reject(error)
    }
)

// 响应拦截器
api.interceptors.response.use(
    response => {
        return response.data
    },
    error => {
        console.error('响应错误:', error)

        if (error.response) {
            const { status, data } = error.response

            if (status === 401) {
                ElMessage.error('登录已过期，请重新登录')
                const userStore = useUserStore()
                userStore.logout()
                window.location.href = '/login'
            } else if (status === 403) {
                ElMessage.error('权限不足')
            } else if (status === 404) {
                ElMessage.error('请求的资源不存在')
            } else if (status === 500) {
                ElMessage.error(data.message || '服务器内部错误')
            } else {
                ElMessage.error(data.message || '请求失败')
            }
        } else if (error.request) {
            ElMessage.error('网络错误，请检查网络连接')
        } else {
            ElMessage.error('请求配置错误')
        }

        return Promise.reject(error)
    }
)

// ============ 认证相关 ============
export const authAPI = {
    login: (credentials) => api.post('/auth/login', credentials),
    getMe: () => api.get('/auth/me'),
}

// ============ 仪表板 ============
export const dashboardAPI = {
    getData: () => api.get('/dashboard'),
}

// ============ 天通终端连接 ============
export const ttcatConnectionAPI = {
    getAll: () => api.get('/ttcat/connections'),
    create: (data) => api.post('/ttcat/connections', data),
    update: (id, data) => api.put(`/ttcat/connections/${id}`, data),
    delete: (id) => api.delete(`/ttcat/connections/${id}`),
}

// ============ 天通短信 ============
export const ttcatSmsAPI = {
    getAll: () => api.get('/ttcat/sms'),
    send: (data) => api.post('/ttcat/sms', data),
    delete: (id) => api.delete(`/ttcat/sms/${id}`),
}

// ============ 天通电话 ============
export const ttcatCallAPI = {
    getAll: () => api.get('/ttcat/calls'),
    initiate: (data) => api.post('/ttcat/calls', data),
    end: (id) => api.put(`/ttcat/calls/${id}/end`),
}

// ============ 北斗短报文 ============
export const beidouMessageAPI = {
    getAll: () => api.get('/beidou/messages'),
    send: (data) => api.post('/beidou/messages', data),
    delete: (id) => api.delete(`/beidou/messages/${id}`),
}

// ============ GPS位置 ============
export const gpsAPI = {
    getPositions: () => api.get('/gps/positions'),
    updatePosition: (data) => api.post('/gps/positions', data),
}

// ============ 轨迹跟踪 ============
export const trackingAPI = {
    getTracks: () => api.get('/tracking/tracks'),
    createTrack: (data) => api.post('/tracking/tracks', data),
    updateTrack: (id, data) => api.put(`/tracking/tracks/${id}`, data),
    deleteTrack: (id) => api.delete(`/tracking/tracks/${id}`),
}

// ============ 视频会议 ============
export const conferenceAPI = {
    getRooms: () => api.get('/conference/rooms'),
    createRoom: (data) => api.post('/conference/rooms', data),
    joinRoom: (id) => api.post(`/conference/rooms/${id}/join`),
    leaveRoom: (id) => api.post(`/conference/rooms/${id}/leave`),
}

// ============ 即时通话 ============
export const instantCallAPI = {
    getGroups: () => api.get('/instant-call/groups'),
    createGroup: (data) => api.post('/instant-call/groups', data),
    startCall: (data) => api.post('/instant-call/start', data),
    endCall: (id) => api.post(`/instant-call/${id}/end`),
}

// ============ 用户管理 ============
export const userAPI = {
    getAll: () => api.get('/users'),
    create: (data) => api.post('/users', data),
    update: (id, data) => api.put(`/users/${id}`, data),
    delete: (id) => api.delete(`/users/${id}`),
    resetPassword: (id, data) => api.put(`/users/${id}/reset-password`, data),
}

// ============ 系统参数 ============
export const parametersAPI = {
    getAll: () => api.get('/parameters'),
    update: (key, value) => api.put(`/parameters/${key}`, { value }),
}

// ============ 设备监控 ============
export const monitoringAPI = {
    getDevices: () => api.get('/monitoring/devices'),
    getDeviceDetail: (id) => api.get(`/monitoring/devices/${id}`),
    restartDevice: (id) => api.post(`/monitoring/devices/${id}/restart`),
}

// ============ 系统日志 ============
export const logsAPI = {
    getAll: (params) => api.get('/logs', { params }),
    create: (data) => api.post('/logs', data),
    clear: () => api.delete('/logs'),
    export: (params) => api.get('/logs/export', { params, responseType: 'blob' }),
}

export default api